import { Modal, Spinner } from "react-bootstrap";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import moment from "jalali-moment";
import {
    CloseCircle,
    Coin1,
    DocumentDownload,
    Eye,
    LampOn,
    Messages,
    Profile2User,
    Share,
} from "iconsax-react";
import { useTranslation } from "react-i18next";
import DBButton from "../../../../component/global/DBButton";
import CxGauges from "./CxGauges";
import { formatCount, formatPercent } from "./helpers";

const METRICS = [
    {
        key: "members",
        icon: Profile2User,
        label: "Organizational.CustomerClub.Members.Metric.Members",
    },
    {
        key: "messages",
        icon: Messages,
        label: "Organizational.CustomerClub.Members.Metric.Messages",
    },
    {
        key: "ideas",
        icon: LampOn,
        label: "Organizational.CustomerClub.Members.Metric.Ideas",
    },
    {
        key: "referral_opens",
        icon: Share,
        label: "Organizational.CustomerClub.Members.Metric.Referrals",
    },
    {
        key: "coins_earned",
        icon: Coin1,
        label: "Organizational.CustomerClub.Members.Metric.Coins",
    },
    {
        key: "content_views",
        icon: Eye,
        label: "Organizational.CustomerClub.Members.Metric.Views",
    },
];

const TICK = { fill: "#667085", fontSize: 11 };
const NPS_COLORS = {
    promoters: "#2f9e6b",
    passives: "#c9a227",
    detractors: "#c2413b",
};
const LOYALTY_KEYS = {
    normal: "Organizational.CustomerClub.Members.Report.Loyalty.Normal",
    active: "Organizational.CustomerClub.Members.Report.Loyalty.Active",
    supportive: "Organizational.CustomerClub.Members.Report.Loyalty.Supportive",
};
const CHANNEL_KEYS = {
    messages: "Organizational.CustomerClub.Members.Metric.Messages",
    ideas: "Organizational.CustomerClub.Members.Metric.Ideas",
    referrals: "Organizational.CustomerClub.Members.Metric.Referrals",
    content_views: "Organizational.CustomerClub.Members.Metric.Views",
    coins: "Organizational.CustomerClub.Members.Metric.Coins",
    surveys: "Organizational.CustomerClub.Members.Metric.Surveys",
};

function monthLabel(row) {
    if (!row?.year || !row?.month) return "";
    return moment(`${row.year}/${row.month}/01`, "YYYY/M/D")
        .locale("fa")
        .format("MMMM YY");
}

function TrendTooltip({ active, payload, label, locale, membersLabel }) {
    if (!active || !payload?.length) return null;
    return (
        <div className="member-report-tooltip">
            <span>{label}</span>
            <strong className="tabular-nums">
                {formatCount(payload[0].value, locale)}
            </strong>
            <em>{membersLabel}</em>
        </div>
    );
}

function ChartTooltip({ active, payload, locale, suffix }) {
    if (!active || !payload?.length) return null;
    const item = payload[0];
    return (
        <div className="member-report-tooltip">
            <span>{item.name || item.payload?.label}</span>
            <strong className="tabular-nums">
                {formatCount(item.value, locale)}
                {suffix || ""}
            </strong>
        </div>
    );
}

function CxSection({ cx, locale, t }) {
    const nps = cx?.nps || {};
    const csat = cx?.csat || {};
    const loyalty = cx?.loyalty || {};
    const engagement = cx?.engagement || {};
    const npsMix = [
        {
            key: "promoters",
            name: t("Organizational.CustomerClub.Members.Report.Nps.Promoters"),
            value: Number(nps.promoters || 0),
            color: NPS_COLORS.promoters,
        },
        {
            key: "passives",
            name: t("Organizational.CustomerClub.Members.Report.Nps.Passives"),
            value: Number(nps.passives || 0),
            color: NPS_COLORS.passives,
        },
        {
            key: "detractors",
            name: t("Organizational.CustomerClub.Members.Report.Nps.Detractors"),
            value: Number(nps.detractors || 0),
            color: NPS_COLORS.detractors,
        },
    ];
    const npsTotal = npsMix.reduce((sum, slice) => sum + slice.value, 0);
    const csatBars = (csat.distribution || []).map((row) => ({
        label: String(row.score),
        count: Number(row.count || 0),
    }));
    const loyaltyBars = (loyalty.levels || []).map((row) => ({
        key: row.key,
        label: t(LOYALTY_KEYS[row.key] || LOYALTY_KEYS.normal),
        count: Number(row.count || 0),
        share: Number(row.share || 0),
    }));
    const channels = (engagement.channels || []).map((row) => ({
        key: row.key,
        label: t(CHANNEL_KEYS[row.key] || row.key),
        rate: Number(row.rate || 0),
        count: Number(row.count || 0),
    }));

    return (
        <section
            className="member-report-cx"
            aria-labelledby="member-report-cx-title"
        >
            <div className="member-report-cx-head">
                <h3 id="member-report-cx-title">
                    {t("Organizational.CustomerClub.Members.Report.Cx.Title")}
                </h3>
                <p>{t("Organizational.CustomerClub.Members.Report.Cx.Subtitle")}</p>
            </div>

            <CxGauges cx={cx} locale={locale} t={t} />

            <div className="member-report-cx-charts">
                <section className="member-report-panel">
                    <h3>
                        {t("Organizational.CustomerClub.Members.Report.Nps.Mix")}
                    </h3>
                    {npsTotal ? (
                        <>
                            <div className="member-report-donut">
                                <ResponsiveContainer width="100%" height={180}>
                                    <PieChart>
                                        <Pie
                                            data={npsMix}
                                            dataKey="value"
                                            nameKey="name"
                                            innerRadius={48}
                                            outerRadius={68}
                                            paddingAngle={3}
                                            stroke="#fff"
                                            strokeWidth={2}
                                        >
                                            {npsMix.map((slice) => (
                                                <Cell
                                                    key={slice.key}
                                                    fill={slice.color}
                                                />
                                            ))}
                                        </Pie>
                                        <Tooltip
                                            content={
                                                <ChartTooltip locale={locale} />
                                            }
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <ul className="member-report-legend">
                                {npsMix.map((slice) => (
                                    <li key={slice.key}>
                                        <i style={{ background: slice.color }} />
                                        <span>{slice.name}</span>
                                        <b className="tabular-nums">
                                            {formatCount(slice.value, locale)}
                                        </b>
                                    </li>
                                ))}
                            </ul>
                            <p className="member-report-note">
                                {nps.source === "recommend_scale"
                                    ? t(
                                          "Organizational.CustomerClub.Members.Report.Nps.Scale"
                                      )
                                    : t(
                                          "Organizational.CustomerClub.Members.Report.Nps.Mapped"
                                      )}
                            </p>
                        </>
                    ) : (
                        <p className="member-report-empty">
                            {t("Organizational.CustomerClub.Members.Report.EmptyCx")}
                        </p>
                    )}
                </section>

                <section className="member-report-panel">
                    <h3>
                        {t(
                            "Organizational.CustomerClub.Members.Report.Csat.Distribution"
                        )}
                    </h3>
                    {csat.sample ? (
                        <div className="member-report-chart member-report-chart-sm">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={csatBars}
                                    margin={{ top: 8, right: 4, left: 0, bottom: 0 }}
                                >
                                    <CartesianGrid
                                        vertical={false}
                                        stroke="#dce3ee"
                                    />
                                    <XAxis
                                        dataKey="label"
                                        tickLine={false}
                                        axisLine={false}
                                        tick={TICK}
                                    />
                                    <YAxis
                                        allowDecimals={false}
                                        tickLine={false}
                                        axisLine={false}
                                        tick={TICK}
                                        width={24}
                                    />
                                    <Tooltip
                                        content={
                                            <ChartTooltip locale={locale} />
                                        }
                                    />
                                    <Bar
                                        dataKey="count"
                                        fill="#174ea6"
                                        radius={[6, 6, 0, 0]}
                                        maxBarSize={28}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <p className="member-report-empty">
                            {t("Organizational.CustomerClub.Members.Report.EmptyCx")}
                        </p>
                    )}
                </section>

                <section className="member-report-panel">
                    <h3>
                        {t(
                            "Organizational.CustomerClub.Members.Report.Loyalty.Levels"
                        )}
                    </h3>
                    {loyaltyBars.some((row) => row.count) ? (
                        <ul className="member-report-groups">
                            {loyaltyBars.map((row) => (
                                <li key={row.key}>
                                    <div className="member-report-group-row">
                                        <span>{row.label}</span>
                                        <b className="tabular-nums">
                                            {formatCount(row.count, locale)}
                                        </b>
                                    </div>
                                    <div
                                        className="member-report-group-track"
                                        aria-hidden="true"
                                    >
                                        <span
                                            className="member-report-group-fill"
                                            style={{
                                                width: `${Math.max(
                                                    row.share,
                                                    row.count ? 4 : 0
                                                )}%`,
                                            }}
                                        />
                                    </div>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="member-report-empty">
                            {t("Organizational.CustomerClub.Members.Report.EmptyCx")}
                        </p>
                    )}
                </section>

                <section className="member-report-panel">
                    <h3>
                        {t(
                            "Organizational.CustomerClub.Members.Report.Engagement.Channels"
                        )}
                    </h3>
                    {channels.length ? (
                        <ul className="member-report-groups">
                            {channels.map((row) => (
                                <li key={row.key}>
                                    <div className="member-report-group-row">
                                        <span>{row.label}</span>
                                        <b className="tabular-nums">
                                            {formatPercent(row.rate, locale)}٪
                                        </b>
                                    </div>
                                    <div
                                        className="member-report-group-track"
                                        aria-hidden="true"
                                    >
                                        <span
                                            className="member-report-group-fill is-engagement"
                                            style={{
                                                width: `${Math.max(
                                                    row.rate,
                                                    row.count ? 4 : 0
                                                )}%`,
                                            }}
                                        />
                                    </div>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="member-report-empty">
                            {t("Organizational.CustomerClub.Members.Report.EmptyCx")}
                        </p>
                    )}
                </section>
            </div>
        </section>
    );
}

export default function MemberReportModal({
    show,
    onHide,
    summary,
    loading,
    onExport,
    exportLoading,
    locale = "fa-IR",
}) {
    const { t } = useTranslation();
    const trend = (summary?.joined_by_month || []).map((row) => ({
        ...row,
        label: monthLabel(row),
    }));
    const groups = summary?.groups || [];
    const groupTotal =
        groups.reduce((sum, group) => sum + (Number(group.count) || 0), 0) || 1;
    const membersLabel = t(
        "Organizational.CustomerClub.Members.Metric.Members"
    );

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            size="lg"
            className="member-report-modal"
            aria-labelledby="member-report-title"
        >
            <Modal.Body>
                <article className="member-report">
                    <header className="member-report-head">
                        <div className="member-report-head-copy">
                            <p className="member-report-kicker">
                                {t(
                                    "Organizational.CustomerClub.Members.Report"
                                )}
                            </p>
                            <h2 id="member-report-title">
                                {t(
                                    "Organizational.CustomerClub.Members.Report.Title"
                                )}
                            </h2>
                            <p>
                                {t(
                                    "Organizational.CustomerClub.Members.Report.Subtitle"
                                )}
                            </p>
                        </div>
                        <div className="member-report-head-actions">
                            <DBButton
                                type="secondary"
                                outlined
                                loading={exportLoading}
                                onClick={onExport}
                                caption={
                                    <>
                                        <DocumentDownload
                                            size={18}
                                            aria-hidden="true"
                                        />
                                        {t(
                                            "Organizational.CustomerClub.Members.Csv"
                                        )}
                                    </>
                                }
                            />
                            <button
                                type="button"
                                className="member-icon-btn"
                                onClick={onHide}
                                aria-label={t(
                                    "Organizational.CustomerClub.Members.Dossier.Close"
                                )}
                            >
                                <CloseCircle size={18} aria-hidden="true" />
                            </button>
                        </div>
                    </header>

                    {loading || !summary ? (
                        <div className="member-report-status" aria-live="polite">
                            <Spinner animation="border" size="sm" />
                            <span>
                                {t(
                                    "Organizational.CustomerClub.Members.Loading"
                                )}
                            </span>
                        </div>
                    ) : (
                        <>
                            <ul className="member-report-metrics">
                                {METRICS.map(({ key, icon: Icon, label }) => (
                                    <li key={key}>
                                        <span
                                            className="member-report-metric-icon"
                                            aria-hidden="true"
                                        >
                                            <Icon size={16} />
                                        </span>
                                        <div className="member-report-metric-copy">
                                            <span>{t(label)}</span>
                                            <strong className="tabular-nums">
                                                {formatCount(
                                                    summary[key],
                                                    locale
                                                )}
                                            </strong>
                                        </div>
                                    </li>
                                ))}
                            </ul>

                            <CxSection
                                cx={summary.cx}
                                locale={locale}
                                t={t}
                            />

                            <div className="member-report-board">
                                <section
                                    className="member-report-panel"
                                    aria-labelledby="member-report-groups-title"
                                >
                                    <h3 id="member-report-groups-title">
                                        {t(
                                            "Organizational.CustomerClub.Members.Report.Groups"
                                        )}
                                    </h3>
                                    {groups.length ? (
                                        <ul className="member-report-groups">
                                            {groups.map((group) => {
                                                const count =
                                                    Number(group.count) || 0;
                                                const share = Math.round(
                                                    (count / groupTotal) * 100
                                                );
                                                return (
                                                    <li
                                                        key={
                                                            group.id || "none"
                                                        }
                                                    >
                                                        <div className="member-report-group-row">
                                                            <span>
                                                                {group.name ||
                                                                    t(
                                                                        "Organizational.CustomerClub.Members.Report.Ungrouped"
                                                                    )}
                                                            </span>
                                                            <b className="tabular-nums">
                                                                {formatCount(
                                                                    count,
                                                                    locale
                                                                )}
                                                            </b>
                                                        </div>
                                                        <div
                                                            className="member-report-group-track"
                                                            aria-hidden="true"
                                                        >
                                                            <span
                                                                className="member-report-group-fill"
                                                                style={{
                                                                    width: `${Math.max(share, count ? 4 : 0)}%`,
                                                                }}
                                                            />
                                                        </div>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    ) : (
                                        <p className="member-report-empty">
                                            {t(
                                                "Organizational.CustomerClub.Not.Found.Title"
                                            )}
                                        </p>
                                    )}
                                </section>

                                <section
                                    className="member-report-panel member-report-panel-chart"
                                    aria-labelledby="member-report-trend-title"
                                >
                                    <h3 id="member-report-trend-title">
                                        {t(
                                            "Organizational.CustomerClub.Members.Report.Trend"
                                        )}
                                    </h3>
                                    {trend.length ? (
                                        <div className="member-report-chart">
                                            <ResponsiveContainer
                                                width="100%"
                                                height="100%"
                                            >
                                                <BarChart
                                                    data={trend}
                                                    margin={{
                                                        top: 12,
                                                        right: 8,
                                                        left: 0,
                                                        bottom: 4,
                                                    }}
                                                >
                                                    <CartesianGrid
                                                        vertical={false}
                                                        stroke="#dce3ee"
                                                        strokeDasharray="0"
                                                    />
                                                    <XAxis
                                                        dataKey="label"
                                                        tickLine={false}
                                                        axisLine={false}
                                                        tick={TICK}
                                                        dy={6}
                                                    />
                                                    <YAxis
                                                        allowDecimals={false}
                                                        tickLine={false}
                                                        axisLine={false}
                                                        tick={TICK}
                                                        width={28}
                                                    />
                                                    <Tooltip
                                                        cursor={{
                                                            fill: "rgba(46, 113, 252, 0.08)",
                                                        }}
                                                        content={
                                                            <TrendTooltip
                                                                locale={locale}
                                                                membersLabel={
                                                                    membersLabel
                                                                }
                                                            />
                                                        }
                                                    />
                                                    <Bar
                                                        dataKey="joined"
                                                        fill="#2e71fc"
                                                        radius={[6, 6, 0, 0]}
                                                        maxBarSize={32}
                                                    />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    ) : (
                                        <p className="member-report-empty">
                                            {t(
                                                "Organizational.CustomerClub.Members.Report.EmptyTrend"
                                            )}
                                        </p>
                                    )}
                                </section>
                            </div>
                        </>
                    )}
                </article>
            </Modal.Body>
        </Modal>
    );
}
